import { computed, ref, type ComputedRef } from 'vue'
import { DEFAULT_LOCALE, LOCALE_COOKIE, type Locale } from './resolver'

/** One namespace = one JSON file per locale (`locales/<locale>/<namespace>.json`), declared in `locales/manifest.json`. */
export type Namespace = 'dstbfs-web' | 'dstbfs-nav' | 'dstbfs-ui'
type Messages = Record<string, string>

// Vite bundles each namespace/locale file as its own lazy chunk (assessment §281: 按 namespace 懒加载).
const loaders = import.meta.glob<{ default: Messages }>('../../locales/*/*.json')

const current = ref<Locale>(DEFAULT_LOCALE)
const loaded = ref<Record<string, Messages>>({})

function key(locale: Locale, ns: Namespace): string {
  return `${locale}/${ns}`
}

/** Loads a namespace for a locale (idempotent). Missing files fail loudly: the manifest parity test guards the set. */
export async function loadNamespace(locale: Locale, ns: Namespace): Promise<void> {
  const k = key(locale, ns)
  if (loaded.value[k]) return
  const path = `../../locales/${locale}/${ns}.json`
  const loader = loaders[path]
  if (!loader) throw new Error(`locale resource missing: ${path}`)
  const mod = await loader()
  loaded.value[k] = mod.default // mutate: concurrent loads must not overwrite each other's namespace (last-writer race)
}

export async function setLocale(locale: Locale, namespaces: readonly Namespace[] = ['dstbfs-web', 'dstbfs-ui']): Promise<void> {
  await Promise.all(namespaces.map((ns) => loadNamespace(locale, ns)))
  current.value = locale
  document.cookie = `${LOCALE_COOKIE}=${encodeURIComponent(locale)}; path=/; max-age=31536000; samesite=lax`
  document.documentElement.lang = locale
}

export const locale: ComputedRef<Locale> = computed(() => current.value)

/**
 * Translates a resource key with `{arg}` placeholders. Unknown keys return the key itself (visible in UI and caught by the
 * hard-coded-copy scan + parity test) — never a silent empty string.
 */
export function t(k: string, args?: Record<string, string | number>): string {
  const ns: Namespace = k.startsWith('dstbfs.nav.') ? 'dstbfs-nav' : k.startsWith('dstbfs.ui.') ? 'dstbfs-ui' : 'dstbfs-web'
  const messages = loaded.value[key(current.value, ns)] ?? loaded.value[key(DEFAULT_LOCALE, ns)]
  const template = messages?.[k] ?? k
  return template.replace(/\{(\w+)\}/g, (_, name: string) => String(args?.[name] ?? `{${name}}`))
}
